/**
 * The store SKU replace warning (Phase 88, D-12): the first upload into a
 * store SKU's platen cell that would replace the file it prints today asks
 * once per product, and never again until the page reloads. The Products
 * tab's Designs tier and the Product updates focused view both ask through
 * here, so the rule and the acceptance are the same on either surface: accept
 * it on one tab and the other does not ask again for that product.
 *
 * Module state on purpose: a product's Designs tier unmounts when its row
 * collapses (and on paging), and the warning must not come back when the row
 * opens again. Cancel accepts nothing, so the next drop asks again.
 */
import { isPlatenSideCovered, type PlatenSizeAssignment } from '@threadheads/shared';
import type { PlatenFileStateBySku } from './platen';

const accepted = new Set<string>();

export function isReplaceNoticeAccepted(productId: string): boolean {
  return accepted.has(productId);
}

export function acceptReplaceNotice(productId: string): void {
  accepted.add(productId);
}

/** Tests only: forget every acceptance (a fresh page load). */
export function resetReplaceNoticesForTests(): void {
  accepted.clear();
}

/**
 * Round 4 (R4-4): the SKUs (of `skus`, in order) whose `side` holds the Small
 * file made from Large. An own file dropped there replaces it and is kept from
 * then on, even when Large changes: the cell says so first (every time; there
 * is nothing to accept for good). From the file state the surface holds.
 */
export function skusWithMadeSmall(
  skus: readonly string[],
  side: 'front' | 'back',
  fileState: PlatenFileStateBySku,
): string[] {
  return skus.filter((sku) =>
    side === 'front' ? fileState[sku]?.frontMadeFromLarge === true : fileState[sku]?.backMadeFromLarge === true,
  );
}

/**
 * The SKUs (of `skus`, in order) that print from an existing file on `side`
 * today: a file link on that side and no exact marker for this platen
 * variant. An upload into the cell replaces that file for them. Read from the
 * file state the surface already holds; no extra request.
 */
export function skusPrintingExistingFile(
  skus: readonly string[],
  side: 'front' | 'back',
  assignment: PlatenSizeAssignment,
  fileState: PlatenFileStateBySku,
): string[] {
  return skus.filter((sku) => {
    const state = fileState[sku];
    const hasFile = side === 'front' ? state?.hasFrontUrl === true : state?.hasBackUrl === true;
    return hasFile && !isPlatenSideCovered(assignment, state?.[side] ?? null);
  });
}
